// @flow

import type {UiState} from '@/types'

import {merge, path} from 'ramda'
import {startDate, endDate, companiesSortOptions,
  modalEventsOptions, recentEventsOptions,
  scheduledExportSortOptions, previousExportSortOptions, descendingOrderKeys} from '@/defaults'
import {getStats} from '../util'

const responseErrorCodePath = path(['response', 'status'])
const generalResponsePreErrorMessage = 'Error occurred while'
const duplicateEntryMessage = (type) => `${type} with given name already exists.`

const initialState: UiState = {
  activeTab: 'home',
  graphStartDate: startDate,
  graphEndDate: endDate,
  portfolioCountryFilter: 'all',
  portfolioSearchFilter: '',
  portfolioPaging: {
    pageNumber: 0,
    pageSize: 50,
  },
  portfolioCompaniesSortOptions: companiesSortOptions,
  companiesByRiskTypePaging: {
    pageNumber: 0,
    pageSize: 50,
  },
  companiesByRiskTypeSortOptions: companiesSortOptions,
  eventsPaging: {
    pageNumber: 0,
    pageSize: 50,
  },
  modalEventsPaging: {
    pageNumber: 0,
    pageSize: 50,
  },
  previousExportsPaging: {
    pageNumber: 0,
    pageSize: 50,
  },
  scheduledExportsPaging: {
    pageNumber: 0,
    pageSize: 50,
  },
  previousExportsSortOptions: previousExportSortOptions,
  scheduledExportsSortOptions: scheduledExportSortOptions,
  modal: {
    shown: false,
    preventConfirm: true,
    error: '',
    message: '',
  },
  shownRiskRating: 'A',
  rulesChanged: false,
  chartType: 'line',
  modalEventsOptions: modalEventsOptions,
  recentEventsOptions: recentEventsOptions,
  confirm: {
    isError: false,
    message: '',
  },
}

const checkSortingOrder = (sortBy) => {
  if (descendingOrderKeys.includes(sortBy)) {
    return 'desc'
  }
  return 'asc'
}
export default (state: UiState = initialState, action: any): UiState => {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.tabName,
      }
    case 'CHANGE_ACTIVE_PORTFOLIO':
      return {
        ...initialState,
        graphStartDate: startDate,
        graphEndDate: endDate,
        activeTab: state.activeTab,
      }
    case 'SET_PORTFOLIO_COUNTRY_FILTER':
      return {
        ...state,
        portfolioCountryFilter: action.code,
      }
    case 'SET_PORTFOLIO_SEARCH_FILTER':
      return {
        ...state,
        portfolioSearchFilter: action.searchTerm,
      }
    case 'SET_PORTFOLIO_PAGE_NUMBER':
      const portfolioPaging = merge(state.portfolioPaging, {
        pageNumber: action.pageNumber,
      })
      return {
        ...state,
        portfolioPaging,
      }
    case 'SET_PORTFOLIO_COMPANIES_SORT_OPTIONS':
      let portfolioCompaniesSortOptions = {}
      const nextSortBy = action.sortBy
      const sortOrder = checkSortingOrder(action.sortBy)
      const nextSortDir = state.portfolioCompaniesSortOptions.sortBy === action.sortBy
        ? (state.portfolioCompaniesSortOptions.sortDir === 'asc' ? 'desc' : 'asc') : sortOrder
      portfolioCompaniesSortOptions = merge(state.portfolioCompaniesSortOptions, {
        sortBy: nextSortBy,
        sortDir: nextSortDir,
      })
      return {
        ...state,
        portfolioCompaniesSortOptions,
      }
    case 'RESET_PORTFOLIO_COMPANIES_SORT_OPTIONS':
      return {
        ...state,
        portfolioCompaniesSortOptions: {
          sortBy: companiesSortOptions.sortBy,
          sortDir: companiesSortOptions.sortDir,
        },
      }
    case 'SET_PORTFOLIO_COMPANIES_RISK_TYPE_PAGE_NUMBER':
      const companiesByRiskTypePaging = merge(state.companiesByRiskTypePaging, {
        pageNumber: action.pageNumber,
      })
      return {
        ...state,
        companiesByRiskTypePaging,
      }
    case 'SET_PORTFOLIO_COMPANIES_RISK_TYPE_SORT_OPTIONS' :
      const {sortBy, sortDir} = action.sortOptions
      const companiesByRiskTypeSortOptions = merge(state.companiesByRiskTypeSortOptions, {
        sortBy,
        sortDir,
      })
      return {
        ...state,
        companiesByRiskTypeSortOptions,
      }
    case 'SET_MODAL_EVENTS_SORT_OPTIONS' :
      const modalSortDir = action.sortDir ? action.sortDir : state.modalEventsOptions.sortBy === action.sortBy
        ? (state.modalEventsOptions.sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
      return {
        ...state,
        modalEventsOptions: {
          sortBy: action.sortBy,
          sortDir: modalSortDir,
        },
      }
    case 'RESET_MODAL_EVENTS_SORT_OPTIONS' :
      return {
        ...state,
        modalEventsOptions: modalEventsOptions,
      }
    case 'SET_EVENTS_PAGE_NUMBER':
      const eventsPaging = merge(state.eventsPaging, {
        pageNumber: action.pageNumber,
      })
      return {
        ...state,
        eventsPaging,
      }
    case 'SET_MODAL_EVENTS_PAGE_NUMBER':
      return {
        ...state,
        modalEventsPaging: {
          ...state.modalEventsPaging,
          pageNumber: action.pageNumber,
        },
      }
    case 'SHOW_MODAL':
      return {
        ...state,
        modal: {
          ...state.modal,
          shown: true,
          component: action.component,
          props: action.props,
          type: action.modalType,
          error: '',
          message: action.props.message,
        },
      }
    case 'CLOSE_MODAL':
      return {
        ...state,
        modal: {
          shown: false,
          preventConfirm: true,
        },
      }
    case 'TOGGLE_RISK_FILTER':
      return {
        ...state,
        shownRiskRating: action.riskRating,
      }
    case 'EDIT_EVENT_RULE':
      return {
        ...state,
        rulesChanged: true,
      }
    case 'SAVE_PORTFOLIO_RULES_SUCCESS':
      return {
        ...state,
        rulesChanged: false,
        confirm: {
          isError: false,
          message: 'Rules saved successfully.',
        },
      }
    case 'SAVE_PORTFOLIO_RULES_FAILURE':
      return {
        ...state,
        confirm: {
          isError: true,
          message: 'Error occurred while saving your rules.',
        },
      }
    case 'SET_CHART_TYPE':
      return {
        ...state,
        chartType: action.chartType,
      }
    case 'SET_GRAPH_DATE':
      return {
        ...state,
        graphStartDate: action.graphDate.startDate,
        graphEndDate: action.graphDate.endDate,
      }
    case 'SET_SCHEDULED_EXPORTS_PAGE_NUMBER':
      const scheduledExportsPaging = merge(state.scheduledExportsPaging, {
        pageNumber: action.pageNumber,
      })
      return {
        ...state,
        scheduledExportsPaging,
      }
    case 'SET_PREVIOUS_EXPORTS_PAGE_NUMBER':
      const previousExportsPaging = merge(state.scheduledExportsPaging, {
        pageNumber: action.pageNumber,
      })
      return {
        ...state,
        previousExportsPaging,
      }
    case 'SET_PREVIOUS_EXPORTS_SORT_OPTIONS':
      const previousExpSortDir = state.previousExportsSortOptions.sortBy === action.sortBy
        ? (state.previousExportsSortOptions.sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
      const previousExportsSortOptions = merge(state.previousExportsSortOptions, {
        sortBy: action.sortBy,
        sortDir: previousExpSortDir,
      })
      return {
        ...state,
        previousExportsSortOptions,
      }
    case 'SET_SCHEDULED_EXPORTS_SORT_OPTIONS':
      const scheduledExpSortDir = state.scheduledExportsSortOptions.sortBy === action.sortBy
        ? (state.scheduledExportsSortOptions.sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
      const scheduledExportsSortOptions = merge(state.scheduledExportsSortOptions, {
        sortBy: action.sortBy,
        sortDir: scheduledExpSortDir,
      })
      return {
        ...state,
        scheduledExportsSortOptions,
      }
    case 'RESET_EXPORTS_SORT_OPTIONS':
      return {
        ...state,
        previousExportsSortOptions: previousExportSortOptions,
        scheduledExportsSortOptions: scheduledExportSortOptions,
      }
    case 'IMPORT_COMPANIES_FILE_SUCCESS':
      return {
        ...initialState,
        activeTab: state.activeTab,
        modal: {
          ...initialState.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Import companies has been initiated.',
        },
      }
    case 'SYNC_COMPANIES_FILE_SUCCESS':
      return {
        ...initialState,
        activeTab: state.activeTab,
        modal: {
          ...initialState.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Syncing companies has been initiated.',
        },
      }
    case 'DELETE_PORTFOLIO_COMPANIES_SUCCESS':
      return {
        ...initialState,
        activeTab: state.activeTab,
        modal: {
          ...initialState.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Selected companies have been deleted successfully.',
        },
      }
    case 'DELETE_PORTFOLIO_COMPANIES_FAILURE':
      return {
        ...initialState,
        activeTab: state.activeTab,
        modal: {
          ...initialState.modal,
          error: '',
        },
        confirm: {
          isError: true,
          message: `${generalResponsePreErrorMessage} deleting selected companies.`,
        },
      }
    case 'ADD_PORTFOLIO_FAILURE':
      const getAddPortfolioError = action.payload.response.data.message
      const addPortfolioError = responseErrorCodePath(action.payload) === 409
        ? (getAddPortfolioError || duplicateEntryMessage('Portfolio'))
        : `${generalResponsePreErrorMessage} creating new portfolio.`

      return {
        ...state,
        modal: {
          ...state.modal,
          error: addPortfolioError,
        },
      }
    case 'ADD_PORTFOLIO_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'New portfolio has been added successfully.',
        },
      }
    case 'GET_PORTFOLIO_DATA_FAILURE':
      const getPortfolioError = `${generalResponsePreErrorMessage} fetching portfolio data. Please try again.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: getPortfolioError,
        },
      }
    case 'UPDATE_PORTFOLIO_FAILURE':
      const getErrorMessage = action.payload.response.data.message
      const updatePortfolioError = responseErrorCodePath(action.payload) === 409
        ? (getErrorMessage || duplicateEntryMessage('Portfolio'))
        : `${generalResponsePreErrorMessage} updating portfolio.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: updatePortfolioError,
        },
      }
    case 'UPDATE_PORTFOLIO_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Portfolio has been updated successfully.',
        },
      }
    case 'DELETE_PORTFOLIO_FAILURE':
      const deletePortfolioError = `${generalResponsePreErrorMessage} deleting portfolio.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: deletePortfolioError,
        },
      }
    case 'DELETE_PORTFOLIO_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Portfolio has been deleted successfully.',
        },
      }
    case 'DELETE_EXPORT_FAILURE':
      const deleteExportError = `${generalResponsePreErrorMessage} deleting export.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: deleteExportError,
        },
      }
    case 'DELETE_EXPORT_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Export has been deleted successfully.',
        },
      }
    case 'ADD_EXPORT_FAILURE':
      const addExportError = responseErrorCodePath(action.payload) === 409
        ? duplicateEntryMessage('Export')
        : `${generalResponsePreErrorMessage} creating new export.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: addExportError,
        },
      }
    case 'ADD_EXPORT_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'New export has been created successfully.',
        },
      }
    case 'SAVE_EXPORT_FAILURE':
      const saveExportError = `${generalResponsePreErrorMessage} updating export.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: saveExportError,
        },
      }
    case 'SAVE_EXPORT_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Export has been updated successfully.',
        },
      }
    case 'UPDATE_COMPANY_DETAILS_FAILURE':
      const saveCompanyError = `${generalResponsePreErrorMessage} updating company details.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: saveCompanyError,
        },
      }
    case 'UPDATE_COMPANY_DETAILS_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Company details have been updated successfully.',
        },
      }
    case 'IMPORT_COMPANIES_FILE_FAILURE':
      const importCompanyError = `${generalResponsePreErrorMessage} importing companies.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: importCompanyError,
        },
      }
    case 'SYNC_COMPANIES_FILE_FAILURE':
      const syncCompanyError = `${generalResponsePreErrorMessage} syncing companies.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: syncCompanyError,
        },
      }
    case 'RESET_CONFIRM_MESSAGE':
      return {
        ...state,
        confirm: {
          ...initialState.confirm,
        },
      }
    case 'COPY_PORTFOLIO_COMPANIES_FAILURE':
      const errorMessageFromCopyService = action.payload && action.payload.response &&
        action.payload.response.data && action.payload.response.data.message
      const copyCompaniesError = `${generalResponsePreErrorMessage} copying selected companies to portfolio(s).`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: true,
          message: errorMessageFromCopyService || copyCompaniesError,
        },
      }
    case 'COPY_PORTFOLIO_COMPANIES_SUCCESS':
      const {failedPortfoliosCompanies} = action.payload.data
      const copySuccessMsg = 'Selected companies have been copied to portfolio(s) successfully.'
      const copyPartlySuccessMsg = 'Some of the selected companies have not copied.'
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: failedPortfoliosCompanies ? copyPartlySuccessMsg : copySuccessMsg,
          stats: failedPortfoliosCompanies ? getStats(action.payload.data) : '',
        },
      }
    case 'MOVE_PORTFOLIO_COMPANIES_FAILURE':
      const errorMessageFromMoveService = action.payload && action.payload.response &&
        action.payload.response.data && action.payload.response.data.message
      const moveCompaniesError = `${generalResponsePreErrorMessage} moving selected companies to portfolio(s).`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: true,
          message: errorMessageFromMoveService || moveCompaniesError,
        },
      }
    case 'MOVE_PORTFOLIO_COMPANIES_SUCCESS':
      const {failedPortfoliosCompanies: failedCompaniesForMove} = action.payload.data
      const moveSuccessMsg = 'Selected companies have been moved to portfolio(s) successfully.'
      const movePartlySuccessMsg = 'Some of the selected companies have not moved.'
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: failedCompaniesForMove ? movePartlySuccessMsg : moveSuccessMsg,
          stats: failedCompaniesForMove ? getStats(action.payload.data) : '',
        },
      }
    case 'GET_PORTFOLIOS_DATA_SUCCESS':
      return {
        ...initialState,
        activeTab: 'portfolios',
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: "Portfolio's data has been refreshed successfully.",
        },
      }
    case 'GET_PORTFOLIOS_DATA_FAILURE':
      const refreshError = `${generalResponsePreErrorMessage} refreshing the portfolio's data.`
      return {
        ...initialState,
        activeTab: 'portfolios',
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: true,
          message: refreshError,
        },
      }
    case 'REFRESH_EXPORTS':
      return {
        ...state,
        previousExportsPaging: {
          ...initialState.previousExportsPaging,
        },
        scheduledExportsPaging: {
          ...initialState.scheduledExportsPaging,
        },
        previousExportsSortOptions: {
          ...initialState.previousExportsSortOptions,
        },
        scheduledExportsSortOptions: {
          ...initialState.scheduledExportsSortOptions,
        },
      }
    case 'FETCH_CUSTOMER_USERS_FAILURE':
      const fetchError = `${generalResponsePreErrorMessage} fetching the users data.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: fetchError,
        },
        confirm: {
          isError: false,
          message: '',
        },
      }
    case 'UPDATE_CUSTOMER_USERS_PERMISSIONS_FAILURE':
      const updationError = `${generalResponsePreErrorMessage} updating the users permissions.`
      return {
        ...state,
        modal: {
          ...state.modal,
          error: updationError,
        },
        confirm: {
          isError: false,
          message: '',
        },
      }
    case 'UPDATE_CUSTOMER_USERS_PERMISSIONS_SUCCESS':
      return {
        ...state,
        modal: {
          ...state.modal,
          error: '',
        },
        confirm: {
          isError: false,
          message: 'Users permissions have been updated successfully.',
        },
      }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/store/ui/reducer.js